import React from "react";
import { apm, reportError } from "../../apm";
import { SUPPORT_LINE } from "../../config/errorMessages";
import { isApmActive } from "../../config/errorRef";

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            errorId: null,
            copied: false
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Log error details to state for display
        this.setState({
            error,
            errorInfo
        });

        this.reportErrorToApm(error, errorInfo);

        // Log to console for test/development
        if (process.env.NODE_ENV === "test") {
            console.group("🚨 React Error Boundary Caught Error");
            console.error("Error:", error);
            console.error("Error Info:", errorInfo);
            console.error("Component Stack:", errorInfo.componentStack);
            console.groupEnd();
        }
    }

    reportErrorToApm = (error, errorInfo) => {
        try {
            // Same short reference format used for API errors, so support only ever has to
            // ask the user for one thing.
            const errorId = reportError(error, {
                kind: "render",
                componentStack: errorInfo.componentStack,
                errorBoundaryName: this.props.name || "UnnamedErrorBoundary",
                userId: this.getUserId(),
                sessionId: this.getSessionId(),
                buildVersion: (window.__ENV && window.__ENV.BUILD_VERSION) || process.env.REACT_APP_VERSION || "unknown",
                environment: process.env.NODE_ENV || "unknown"
            });

            this.setState({ errorId });
        } catch (reportingError) {
            console.error("Failed to report error to APM:", reportingError);
        }
    };

    handleCopyErrorId = () => {
        try {
            navigator.clipboard?.writeText(this.state.errorId);
            this.setState({ copied: true });
            setTimeout(() => this.setState({ copied: false }), 2000);
        } catch (_) {
            /* clipboard unavailable — the reference is still readable on screen */
        }
    };

    getUserId = () => {
        // Try to get user ID from various sources
        try {
            return localStorage.getItem("userId") || sessionStorage.getItem("userId") || window.currentUserId || null;
        } catch {
            return null;
        }
    };

    getSessionId = () => {
        // Try to get session ID from various sources
        try {
            return sessionStorage.getItem("sessionId") || localStorage.getItem("sessionId") || window.sessionId || null;
        } catch {
            return null;
        }
    };

    handleReload = () => {
        // Track the reload action
        if (apm) {
            apm.startTransaction("ErrorBoundaryReload", "action");
        }

        window.location.reload();
    };

    handleGoHome = () => {
        // Track the go home action
        if (apm) {
            apm.startTransaction("ErrorBoundaryGoHome", "action");
        }

        window.location.href = "/";
    };

    render() {
        if (this.state.hasError) {
            // Custom fallback UI
            if (this.props.fallback) {
                return this.props.fallback(this.state.error, this.state.errorInfo, this.state.errorId);
            }

            // Default fallback UI
            return (
                <div
                    className="error-boundary-container"
                    style={{
                        minHeight: "100vh",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f8f9fa",
                        padding: "20px"
                    }}
                >
                    <div
                        className="error-boundary-content"
                        style={{
                            maxWidth: "600px",
                            textAlign: "center",
                            backgroundColor: "white",
                            padding: "40px",
                            borderRadius: "8px",
                            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)"
                        }}
                    >
                        <div style={{ fontSize: "64px", marginBottom: "20px" }}>⚠️</div>

                        <h1
                            style={{
                                color: "#dc3545",
                                marginBottom: "16px",
                                fontSize: "24px",
                                fontWeight: "bold"
                            }}
                        >
                            Oops! Something went wrong
                        </h1>

                        <p
                            style={{
                                color: "#6c757d",
                                marginBottom: "24px",
                                fontSize: "16px",
                                lineHeight: "1.5"
                            }}
                        >
                            We're sorry, but an unexpected error occurred. We've logged the details and we're looking into it.
                        </p>

                        {this.state.errorId && isApmActive() && (
                            <p
                                style={{
                                    color: "#6c757d",
                                    fontSize: "14px",
                                    marginBottom: "24px",
                                    backgroundColor: "#f8f9fa",
                                    padding: "8px",
                                    borderRadius: "4px"
                                }}
                            >
                                {SUPPORT_LINE} and quote{" "}
                                <button
                                    type="button"
                                    onClick={this.handleCopyErrorId}
                                    aria-label={`Copy reference ${this.state.errorId}`}
                                    title="Copy reference"
                                    style={{
                                        fontFamily: "monospace",
                                        fontSize: "14px",
                                        border: "1px solid #ced4da",
                                        borderRadius: "3px",
                                        background: "#fff",
                                        padding: "2px 8px",
                                        cursor: "pointer"
                                    }}
                                >
                                    {this.state.errorId} {this.state.copied ? "✓" : "⧉"}
                                </button>
                            </p>
                        )}

                        <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
                            <button
                                onClick={this.handleReload}
                                style={{
                                    backgroundColor: "#007bff",
                                    color: "white",
                                    border: "none",
                                    padding: "12px 24px",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontSize: "16px",
                                    fontWeight: "500"
                                }}
                                onMouseOver={e => (e.target.style.backgroundColor = "#0056b3")}
                                onMouseOut={e => (e.target.style.backgroundColor = "#007bff")}
                            >
                                Reload Page
                            </button>

                            <button
                                onClick={this.handleGoHome}
                                style={{
                                    backgroundColor: "#6c757d",
                                    color: "white",
                                    border: "none",
                                    padding: "12px 24px",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontSize: "16px",
                                    fontWeight: "500"
                                }}
                                onMouseOver={e => (e.target.style.backgroundColor = "#545b62")}
                                onMouseOut={e => (e.target.style.backgroundColor = "#6c757d")}
                            >
                                Go Home
                            </button>
                        </div>

                        {process.env.NODE_ENV === "test" && this.state.error && (
                            <details style={{ marginTop: "24px", textAlign: "left" }}>
                                <summary style={{ cursor: "pointer", marginBottom: "8px" }}>🔍 Developer Details</summary>
                                <pre
                                    style={{
                                        backgroundColor: "#f8f9fa",
                                        padding: "16px",
                                        borderRadius: "4px",
                                        overflow: "auto",
                                        fontSize: "12px",
                                        border: "1px solid #dee2e6"
                                    }}
                                >
                                    <strong>Error:</strong> {this.state.error.message}
                                    {"\n\n"}
                                    <strong>Stack:</strong>
                                    {"\n"}
                                    {this.state.error.stack}
                                    {"\n\n"}
                                    <strong>Component Stack:</strong>
                                    {"\n"}
                                    {this.state.errorInfo?.componentStack}
                                </pre>
                            </details>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
